import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { useHistory, Link, useLocation } from "react-router-dom";
import { DeleteOutline, EditOutlined } from "@material-ui/icons";
import { Grid, IconButton, Button, Typography } from "@material-ui/core";
import { useSnackbar } from "contexts/SnackbarContext";
import { snackbarTypes } from "constants/snackbar";
import { errorMessages } from "constants/errorMessages";
import { routes } from "constants/routes";
import AmoConfirmPopover from "components/AmoConfirmPopover";
import colors from "constants/colors";
import * as helpListService from "api/services/helpListService";
import { copyPageUrl } from "utils/url";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useIsMounted } from "hooks/useIsMounted";
import AmoTable from "components/table/AmoTable";
import { tableDefaultActions } from "constants/amoTableConstants";
import { getSortingComparator, stableSort } from "utils/data";

const useStyles = makeStyles((theme) => ({
  actionIcon: {
    fontSize: "1.5rem",
  },
  copyButton: {
    width: "10rem",
    height: "3.25rem",
  },
  textLink: {
    color: colors.green.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  rowFont: {
    fontSize: "0.875rem",
  },
}));

/**
 * The AMO Help Pages, for managing help articles.
 *
 * @param {object} props
 * @returns {React.Component} AMO Help Pages component.
 */
const Pages = (props) => {
  const { tableTitleComponent } = props;

  const classes = useStyles();
  const mounted = useIsMounted();
  const history = useHistory();
  const { search } = useLocation();
  const { showSnackbar } = useSnackbar();
  const globalClasses = useGlobalStyles();

  const [deletePopover, setDeletePopover] = useState(false);
  const [pageData, setPageData] = useState([]);
  const [deletePermalink, setDeletePermalink] = useState(null);
  const [order, setOrder] = useState();
  const [orderBy, setOrderBy] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [focusedItem, setFocusedItem] = useState({});
  const rowRefs = useRef({});

  const tableColumns = [
    {
      fieldKey: "pageComponent",
      label: "Page Title",
      widthPerc: 20,
      customRender: ({ id, permalink, title }) => (
        <Link
          data-testid="PagesTableNameLink"
          to={`${routes.contentManagement.help.editPage.replace(
            ":permalink",
            permalink
          )}${search}`}
          ref={(element) => {
            rowRefs.current[id] = element;
          }}
          className={globalClasses.tableActionCell}
          onFocus={() => setFocusedItem({ id })}
        >
          <Typography
            className={classes.rowFont}
            color="primary"
            variant="body2"
          >
            {title}
          </Typography>
        </Link>
      ),
      isCustom: true,
    },
    {
      fieldKey: "lastEdited",
      label: "Last Edited",
      widthPerc: 30,
    },
    {
      fieldKey: "published",
      label: "Published?",
      widthPerc: 12,
    },
    {
      fieldKey: "sortOrder",
      label: "Sort",
      widthPerc: 10,
    },
    {
      fieldKey: "actions",
      hideLabel: true,
      hideSearch: true,
      widthPerc: 28,
      customRender: ({ id, permalink, pageTitle }) => (
        <Grid container direction="row" spacing={2}>
          <Grid item>
            <Button
              data-testid={`pagesTableCopyButton${permalink}`}
              className={classes.copyButton}
              color="primary"
              variant="outlined"
              size="medium"
              onClick={(event) => copyURL(event, permalink)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  copyURL(event, permalink);
                }
              }}
              disableElevation
            >
              <Typography align="center" variant="body2">
                Copy URL
              </Typography>
            </Button>
          </Grid>
          <Grid item>
            <IconButton
              data-testid={`pagesTableEditButton${permalink}`}
              component={Link}
              to={`${routes.contentManagement.help.editPage.replace(
                ":permalink",
                permalink
              )}${search}`}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  history.push(
                    `/content-management/help/page/${permalink}${search}`
                  );
                }
              }}
              onFocus={() => setFocusedItem({ id })}
            >
              <EditOutlined color="primary" className={classes.actionIcon} />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton
              data-testid={`pagesTableRemoveButton${permalink}`}
              onClick={(event) => onRemove(event, permalink)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  onRemove(event, permalink);
                }
              }}
            >
              <DeleteOutline color="secondary" className={classes.actionIcon} />
            </IconButton>
          </Grid>
        </Grid>
      ),
      isCustom: true,
    },
  ];

  const onRemove = async (event, permalink) => {
    event.stopPropagation(); // prevents the AmoTable click event from propagating - prevents the delete + copy function from sending the user to the edit page
    setDeletePermalink(permalink);
    setDeletePopover(true);
  };

  const removeEvent = async () => {
    try {
      await helpListService.remove(deletePermalink);

      if (!mounted.current) {
        return;
      }

      showSnackbar("Successfully deleted");
      getData();
      setDeletePopover(false);
    } catch (error) {
      console.error(error);

      if (!mounted.current) {
        return;
      }

      showSnackbar(errorMessages.generic, snackbarTypes.error);
    }
  };

  const copyURL = (event, permalink) => {
    event.stopPropagation(); // prevents the AmoTable click event from propagating - prevents the delete + copy function from sending the user to the edit page
    copyPageUrl(routes.help.page.replace(":permalink", permalink));
    showSnackbar("URL Copied");
  };

  // function that gets the required HelpPage data
  const getData = async () => {
    setIsLoading(true);

    try {
      const { data } = await helpListService.getAll();

      if (!mounted.current) {
        return;
      }

      const mappedResult = data.map((item) => ({
        ...item,
        published: item.isPublished ? "Yes" : "No",
      }));

      setPageData(mappedResult);
    } catch (error) {
      console.error(error);

      if (!mounted.current) {
        return;
      }

      if (error.response.data.status === 404) {
        setPageData([]);
      } else {
        showSnackbar(errorMessages.generic, snackbarTypes.error);
      }
    }

    setIsLoading(false);
    setIsLoaded(true);
  };

  // this effect will make a call to the API to get the Page list
  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      // If the data is loaded, sort the data
      const sortedResult = stableSort(
        pageData,
        getSortingComparator(
          order,
          orderBy === "pageComponent" ? "title" : orderBy
        )
      );

      setPageData(sortedResult);
    }
  }, [isLoaded, order, orderBy]);

  const tableActions = {
    titleComponent: tableTitleComponent,
    actions: [tableDefaultActions.new],
    actionsCustomConfigs: {
      [tableDefaultActions.new]: {
        label: "New Page",
        onClick: () => {
          history.push(`${routes.contentManagement.help.newPage}${search}`);
        },
        style: { width: "15rem" },
      },
    },
  };

  const handleRequestSort = (fieldKey, newOrder) => {
    setOrder(newOrder);
    setOrderBy(fieldKey);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <>
        <AmoConfirmPopover
          data-testid="deletePagesPopup"
          open={deletePopover}
          title="Delete Page"
          text="Are you sure you want to delete this Page?"
          yesButtonFunction={removeEvent}
          yesButtonVariant="contained"
          noButtonFunction={() => setDeletePopover(false)}
          closePopover={() => setDeletePopover(false)}
        />
        <AmoTable
          id="pages-table"
          items={pageData}
          columns={tableColumns}
          defaultOrderBy="sortOrder"
          onRequestSort={handleRequestSort}
          shouldUseQueryString
          disableFilters
          actionsProps={tableActions}
          onScroll={(item) => {
            rowRefs.current?.[item.id]?.focus();
          }}
          focusedItem={focusedItem}
          isLoading={isLoading}
        />
      </>
    </form>
  );
};

Pages.propTypes = {
  tableTitleComponent: PropTypes.node,
};

Pages.defaultProps = {
  tableTitleComponent: null,
};

export default Pages;
